// frontend/src/widgets/JobCreateDrawer.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-08-22
// Updated:      2025-08-22
//
// Description:
//   Slide-over drawer to create Jobs in 5 guided steps.
//   - Uses axiosInstance (relative paths)
//   - Theme aware (bg-accent, btnAction*, btnGeneral*)
//   - Pulls taxonomy (type/subtype/subtype2), customers/contacts, locations/resources
//   - Optional templates (list/apply) via /api/job-templates
//   - Exports as a widget: shows a small card with “New Job” which opens the drawer

import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";
import { getWidgetTitle } from "../utils/widgetUtils";

// ---------- small UI helpers ----------
const TextInput = (p) => (
  <input
    {...p}
    className={
      "text-sm text-black border border-gray-300 bg-white rounded px-2 py-1 w-full " +
      (p.className || "")
    }
  />
);
const Select = (p) => (
  <select
    {...p}
    className={
      "text-sm text-black border border-gray-300 bg-white rounded px-2 py-1 w-full " +
      (p.className || "")
    }
  />
);
const TextArea = (p) => (
  <textarea
    {...p}
    className={
      "text-sm text-black border border-gray-300 bg-white rounded px-2 py-1 w-full min-h-[100px] " +
      (p.className || "")
    }
  />
);
const NumberInput = (p) => (
  <input
    type="number"
    {...p}
    className={
      "text-sm text-black border border-gray-300 bg-white rounded px-2 py-1 " +
      (p.className || "w-28")
    }
  />
);

const StepHeader = ({ step, total, title, subtitle }) => (
  <div className="px-6 pt-5 pb-3 border-b bg-white sticky top-0 z-10">
    <div className="text-xs text-gray-500">Step {step} of {total}</div>
    <div className="text-lg font-semibold">{title}</div>
    {subtitle ? <div className="text-xs text-gray-500 mt-1">{subtitle}</div> : null}
  </div>
);

// ---------- Drawer Shell ----------
const Drawer = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-full md:w-[720px] bg-white shadow-2xl z-50 flex flex-col">
        {children}
      </div>
    </>
  );
};

// ---------- Main Widget ----------
const JobCreateDrawer = ({ className }) => {
  const { user } = useAuth();
  const { bump } = useDashboardRefresh();

  // drawer + steps
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const totalSteps = 5;

  // lookups
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [subtypes2, setSubtypes2] = useState([]);
  const [locations, setLocations] = useState([]);
  const [resources, setResources] = useState([]);
  const [templates, setTemplates] = useState([]);

  // form state
  const [title, setTitle] = useState("");
  const [jobRef, setJobRef] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [contactId, setContactId] = useState("");
  const [priority, setPriority] = useState("normal");
  const [slaId, setSlaId] = useState(""); // optional
  const [tags, setTags] = useState(""); // comma separated

  const [jobTypeId, setJobTypeId] = useState(null);
  const [jobSubtypeId, setJobSubtypeId] = useState(null);
  const [jobSubtype2Id, setJobSubtype2Id] = useState(null);

  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [selectedResourceIds, setSelectedResourceIds] = useState([]);
  const [estimatedHours, setEstimatedHours] = useState("");
  const [deviceCount, setDeviceCount] = useState("");

  const [description, setDescription] = useState("");

  // ui
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // ---------- Lookups ----------
  useEffect(() => {
    const run = async () => {
      try {
        const [c, t, l, r, tpl] = await Promise.all([
          axiosInstance.get("customers"),
          axiosInstance.get("job-types?sort=name:asc"),
          axiosInstance.get("locations"),
          axiosInstance.get("resources"),
          axiosInstance.get("job-templates?sort=name:asc").catch(() => ({ data: [] })), // optional
        ]);
        setCustomers(Array.isArray(c.data) ? c.data : []);
        setTypes(Array.isArray(t.data) ? t.data : []);
        setLocations(Array.isArray(l.data) ? l.data : []);
        setResources(Array.isArray(r.data) ? r.data : []);
        setTemplates(Array.isArray(tpl.data) ? tpl.data : []);
      } catch (e) {
        console.error("[job-create] lookups failed:", e?.response?.data || e?.message || e);
      }
    };
    if (open) run();
  }, [open]);

  useEffect(() => {
    if (!customerId) { setContacts([]); setContactId(""); return; }
    axiosInstance.get(`contacts?customer_id=${customerId}`)
      .then(res => setContacts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setContacts([]));
  }, [customerId]);

  useEffect(() => {
    if (!jobTypeId) { setSubtypes([]); setJobSubtypeId(null); setSubtypes2([]); setJobSubtype2Id(null); return; }
    axiosInstance.get(`job-subtypes?job_type_id=${jobTypeId}&sort=name:asc`)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setSubtypes(data);
        if (!data.some(d => d.id === jobSubtypeId)) setJobSubtypeId(null);
        setSubtypes2([]); setJobSubtype2Id(null);
      })
      .catch(() => { setSubtypes([]); setJobSubtypeId(null); setSubtypes2([]); setJobSubtype2Id(null); });
  }, [jobTypeId]);

  useEffect(() => {
    if (!jobSubtypeId) { setSubtypes2([]); setJobSubtype2Id(null); return; }
    axiosInstance.get(`job-subtypes2?job_subtype_id=${jobSubtypeId}&sort=name:asc`)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setSubtypes2(data);
        if (!data.some(d => d.id === jobSubtype2Id)) setJobSubtype2Id(null);
      })
      .catch(() => { setSubtypes2([]); setJobSubtype2Id(null); });
  }, [jobSubtypeId]);

  // ---------- Templates ----------
  const applyTemplate = (tplId) => {
    const tpl = templates.find(t => String(t.id) === String(tplId));
    if (!tpl) return;
    const p = tpl.payload || {};
    setTitle(p.title || "");
    setJobRef(p.job_ref || "");
    setPriority(p.priority || "normal");
    setSlaId(p.sla_id ? String(p.sla_id) : "");
    setTags((p.tags || []).join(", "));
    setJobTypeId(p.job_type_id || null);
    setJobSubtypeId(p.job_subtype_id || null);
    setJobSubtype2Id(p.job_subtype2_id || null);
    setStartAt(p.start_at ? p.start_at.slice(0,16) : "");
    setEndAt(p.end_at ? p.end_at.slice(0,16) : "");
    setSelectedLocationIds(p.location_ids || []);
    setSelectedResourceIds(p.resource_ids || []);
    setEstimatedHours(p.estimated_hours || "");
    setDeviceCount(p.device_count || "");
    setDescription(p.description || "");
  };

  // ---------- Submit ----------
  const canNext = useMemo(() => {
    if (step === 1) return !!(title && customerId);
    if (step === 2) return !!jobTypeId; // allow subtype(s) optional
    if (step === 3) return true;
    if (step === 4) return true;
    return true;
  }, [step, title, customerId, jobTypeId]);

  const handleCreate = async () => {
    setErr(""); setOk(""); setLoading(true);
    try {
      const payload = {
        title: title || jobRef || "New Job",
        job_ref: jobRef || undefined,
        customer_id: customerId ? Number(customerId) : undefined,
        contact_id: contactId ? Number(contactId) : null,
        priority,
        sla_id: slaId ? Number(slaId) : null,
        status: "scheduled", // or "draft" if you prefer
        job_type_id: jobTypeId || null,
        job_subtype_id: jobSubtypeId || null,
        job_subtype2_id: jobSubtype2Id || null,
        start_at: startAt ? new Date(startAt).toISOString() : null,
        end_at: endAt ? new Date(endAt).toISOString() : null,
        location_ids: selectedLocationIds,
        resource_ids: selectedResourceIds,
        estimated_hours: estimatedHours ? Number(estimatedHours) : null,
        device_count: deviceCount ? Number(deviceCount) : null,
        tags: tags
          ? tags.split(",").map(s => s.trim()).filter(Boolean)
          : [],
        description,
        tenant_id: user?.tenant_id,
        processed_by: user?.id,
      };

      const res = await axiosInstance.post("jobs", payload);
      setOk(`Job created (ID ${res?.data?.id || "?"})`);
      bump(); // refresh Jobs list widget
      // reset, keep drawer open on Review success
      setTimeout(() => {
        setOpen(false);
        setStep(1);
        setTitle(""); setJobRef("");
        setCustomerId(""); setContactId("");
        setPriority("normal"); setSlaId(""); setTags("");
        setJobTypeId(null); setJobSubtypeId(null); setJobSubtype2Id(null);
        setStartAt(""); setEndAt("");
        setSelectedLocationIds([]); setSelectedResourceIds([]);
        setEstimatedHours(""); setDeviceCount(""); setDescription("");
        setOk("");
      }, 900);
    } catch (e) {
      const m = e?.response?.data?.detail || e?.message || "Failed to create job.";
      setErr(m);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Render ----------
  return (
    <div
      className={
        "h-full w-full overflow-hidden rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col " +
        (className || "")
      }
    >
      {/* Header bar */}
      <div className="bg-accent text-white px-4 py-2 flex items-center justify-between">
        <span className="text-sm font-semibold">
          {getWidgetTitle("job_create") || "Create Job"}
        </span>
        <button
          onClick={() => { setOpen(true); setStep(1); }}
          className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-xs font-semibold px-3 py-1 rounded shadow"
        >
          New Job
        </button>
      </div>

      {/* Body placeholder */}
      <div className="p-3 text-sm text-gray-600">
        Use the “New Job” button to open the guided creator. You can save templates and reuse them.
      </div>

      {/* Drawer */}
      <Drawer open={open} onClose={() => setOpen(false)}>
        {/* Drawer header */}
        <div className="px-6 py-4 bg-accent text-white flex items-center justify-between">
          <div className="font-semibold">Create Job</div>
          <button onClick={() => setOpen(false)} className="text-white underline text-sm">Close</button>
        </div>

        {/* Steps */}
        <div className="flex-1 overflow-auto">

          {/* STEP 1: Basics */}
          {step === 1 && (
            <>
              <StepHeader step={1} total={totalSteps} title="Basics" subtitle="Job identity and client" />
              <div className="p-6 grid gap-4">
                <div>
                  <label className="text-sm text-gray-700">Title</label>
                  <TextInput value={title} onChange={(e)=>setTitle(e.target.value)} required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm text-gray-700">Job Ref</label>
                    <TextInput value={jobRef} onChange={(e)=>setJobRef(e.target.value)} placeholder="(auto or custom)" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Priority</label>
                    <Select value={priority} onChange={(e)=>setPriority(e.target.value)}>
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">SLA (optional)</label>
                    <Select value={slaId} onChange={(e)=>setSlaId(e.target.value)}>
                      <option value="">-- None --</option>
                      <option value="1">Standard</option>
                      <option value="2">24h</option>
                      <option value="3">4h Response</option>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-700">Customer</label>
                    <Select value={customerId} onChange={(e)=>setCustomerId(e.target.value)} required>
                      <option value="">-- Select Customer --</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Contact (optional)</label>
                    <Select value={contactId} onChange={(e)=>setContactId(e.target.value)} disabled={!customerId || !contacts.length}>
                      <option value="">-- Select Contact --</option>
                      {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-700">Tags (comma separated)</label>
                  <TextInput value={tags} onChange={(e)=>setTags(e.target.value)} placeholder="mcc, install, night-shift" />
                </div>

                <div>
                  <label className="text-sm text-gray-700">Template</label>
                  <div className="flex gap-2">
                    <Select onChange={(e)=>applyTemplate(e.target.value)} defaultValue="">
                      <option value="">-- Choose Template --</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </Select>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* STEP 2: Work Type */}
          {step === 2 && (
            <>
              <StepHeader step={2} total={totalSteps} title="Work Type" subtitle="Select Type → Subtype → Subtype2" />
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Types */}
                <div className="border rounded min-h-[280px] flex flex-col">
                  <div className="px-3 py-2 border-b text-sm font-semibold">Types</div>
                  <div className="flex-1 overflow-auto">
                    <ul>
                      {types.map(t => (
                        <li key={t.id}
                            onClick={()=>setJobTypeId(t.id)}
                            className={"px-3 py-2 border-b cursor-pointer " + (jobTypeId===t.id ? "bg-blue-50" : "bg-white")}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{t.name}</span>
                            <span className="text-xs text-gray-500">({t.short_code})</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {/* Subtypes */}
                <div className="border rounded min-h-[280px] flex flex-col">
                  <div className="px-3 py-2 border-b text-sm font-semibold">Subtypes</div>
                  <div className="flex-1 overflow-auto">
                    <ul>
                      {subtypes.map(s => (
                        <li key={s.id}
                            onClick={()=>setJobSubtypeId(s.id)}
                            className={"px-3 py-2 border-b cursor-pointer " + (jobSubtypeId===s.id ? "bg-blue-50" : "bg-white")}>
                          <div className="font-medium">{s.name}</div>
                          <div className="text-[11px] text-gray-600 line-clamp-1">{s.description || "\u00A0"}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {/* Subtypes2 */}
                <div className="border rounded min-h-[280px] flex flex-col">
                  <div className="px-3 py-2 border-b text-sm font-semibold">Subtype 2</div>
                  <div className="flex-1 overflow-auto">
                    <ul>
                      {subtypes2.map(s2 => (
                        <li key={s2.id}
                            onClick={()=>setJobSubtype2Id(s2.id)}
                            className={"px-3 py-2 border-b cursor-pointer " + (jobSubtype2Id===s2.id ? "bg-blue-50" : "bg-white")}>
                          <div className="font-medium">{s2.name}</div>
                          <div className="text-[11px] text-gray-600 line-clamp-1">{s2.description || "\u00A0"}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* STEP 3: Scope & Schedule */}
          {step === 3 && (
            <>
              <StepHeader step={3} total={totalSteps} title="Scope & Scheduling" />
              <div className="p-6 grid gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-700">Start</label>
                    <input type="datetime-local" value={startAt} onChange={(e)=>setStartAt(e.target.value)}
                           className="text-sm text-black border border-gray-300 bg-white rounded px-2 py-1 w-full"/>
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">End</label>
                    <input type="datetime-local" value={endAt} onChange={(e)=>setEndAt(e.target.value)}
                           className="text-sm text-black border border-gray-300 bg-white rounded px-2 py-1 w-full"/>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-700">Locations</label>
                    <div className="border rounded p-2 min-h-[44px]">
                      <div className="flex flex-wrap gap-2">
                        {locations.map(l => {
                          const checked = selectedLocationIds.includes(l.id);
                          return (
                            <label key={l.id} className={"text-xs px-2 py-1 rounded border cursor-pointer " + (checked ? "bg-blue-50 border-blue-300" : "bg-white")}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e)=> {
                                  setSelectedLocationIds(prev =>
                                    e.target.checked ? [...prev, l.id] : prev.filter(id => id !== l.id)
                                  );
                                }}
                                className="mr-2"
                              />
                              {l.name || l.address || `Location ${l.id}`}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Resources / Assignees</label>
                    <div className="border rounded p-2 min-h-[44px]">
                      <div className="flex flex-wrap gap-2">
                        {resources.map(r => {
                          const checked = selectedResourceIds.includes(r.id);
                          return (
                            <label key={r.id} className={"text-xs px-2 py-1 rounded border cursor-pointer " + (checked ? "bg-blue-50 border-blue-300" : "bg-white")}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e)=> {
                                  setSelectedResourceIds(prev =>
                                    e.target.checked ? [...prev, r.id] : prev.filter(id => id !== r.id)
                                  );
                                }}
                                className="mr-2"
                              />
                              {r.name || r.code || `Res ${r.id}`}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm text-gray-700">Estimated Hours</label>
                    <NumberInput value={estimatedHours} onChange={(e)=>setEstimatedHours(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700"># Devices</label>
                    <NumberInput value={deviceCount} onChange={(e)=>setDeviceCount(e.target.value)} />
                  </div>
                  <div className="flex items-end text-xs text-gray-500">
                    Optional; can be refined later.
                  </div>
                </div>
              </div>
            </>
          )}

          {/* STEP 4: Details */}
          {step === 4 && (
            <>
              <StepHeader step={4} total={totalSteps} title="Details & Attachments" />
              <div className="p-6 grid gap-4">
                <div>
                  <label className="text-sm text-gray-700">Description</label>
                  <TextArea value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="Describe the scope, access restrictions, checklists, etc." />
                </div>
                <div className="text-xs text-gray-500">
                  Attachments: use the existing “Upload” widget after creating, or drop files into the job later.
                </div>
              </div>
            </>
          )}

          {/* STEP 5: Review */}
          {step === 5 && (
            <>
              <StepHeader step={5} total={totalSteps} title="Review & Create" />
              <div className="p-6 grid gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded p-3">
                    <div className="font-semibold text-sm mb-2">Basics</div>
                    <div className="text-xs text-gray-700 space-y-1">
                      <div><b>Title:</b> {title || "-"}</div>
                      <div><b>Ref:</b> {jobRef || "-"}</div>
                      <div><b>Customer:</b> {customers.find(c=>String(c.id)===String(customerId))?.name || "-"}</div>
                      <div><b>Contact:</b> {contacts.find(c=>String(c.id)===String(contactId)) ? `${contacts.find(c=>String(c.id)===String(contactId)).first_name} ${contacts.find(c=>String(c.id)===String(contactId)).last_name}` : "-"}</div>
                      <div><b>Priority:</b> {priority}</div>
                      <div><b>SLA:</b> {slaId || "—"}</div>
                      <div><b>Tags:</b> {tags || "—"}</div>
                    </div>
                  </div>
                  <div className="border rounded p-3">
                    <div className="font-semibold text-sm mb-2">Work Type</div>
                    <div className="text-xs text-gray-700 space-y-1">
                      <div><b>Type:</b> {types.find(t=>t.id===jobTypeId)?.name || "—"}</div>
                      <div><b>Subtype:</b> {subtypes.find(s=>s.id===jobSubtypeId)?.name || "—"}</div>
                      <div><b>Subtype2:</b> {subtypes2.find(s=>s.id===jobSubtype2Id)?.name || "—"}</div>
                    </div>
                  </div>
                  <div className="border rounded p-3">
                    <div className="font-semibold text-sm mb-2">Schedule & Scope</div>
                    <div className="text-xs text-gray-700 space-y-1">
                      <div><b>Start:</b> {startAt || "—"}</div>
                      <div><b>End:</b> {endAt || "—"}</div>
                      <div><b>Locations:</b> {selectedLocationIds.length ? selectedLocationIds.join(", ") : "—"}</div>
                      <div><b>Resources:</b> {selectedResourceIds.length ? selectedResourceIds.join(", ") : "—"}</div>
                      <div><b>Est. Hours:</b> {estimatedHours || "—"}</div>
                      <div><b># Devices:</b> {deviceCount || "—"}</div>
                    </div>
                  </div>
                  <div className="border rounded p-3">
                    <div className="font-semibold text-sm mb-2">Description</div>
                    <div className="text-xs text-gray-700 whitespace-pre-wrap">
                      {description || "—"}
                    </div>
                  </div>
                </div>

                {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                {ok && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{ok}</div>}
              </div>
            </>
          )}
        </div>

        {/* Sticky footer */}
        <div className="px-6 py-3 border-t bg-white flex items-center justify-between">
          <div className="text-xs text-gray-500">Use Back/Next to navigate. Fields are validated per step.</div>
          <div className="flex gap-2">
            <button
              onClick={()=> setStep(s => Math.max(1, s-1))}
              disabled={step===1}
              className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick disabled:opacity-50 text-white text-xs font-semibold px-3 py-1 rounded shadow"
            >
              Back
            </button>
            {step < totalSteps ? (
              <button
                onClick={()=> canNext && setStep(s => Math.min(totalSteps, s+1))}
                disabled={!canNext}
                className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick disabled:opacity-50 text-white text-xs font-semibold px-3 py-1 rounded shadow"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={loading || !title || !customerId || !jobTypeId}
                className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick disabled:opacity-50 text-white text-xs font-semibold px-3 py-1 rounded shadow"
              >
                {loading ? "Creating…" : "Create Job"}
              </button>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default JobCreateDrawer;
